import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Share2, Copy, Trash2, EyeOff } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { CreateShareForm } from '@/components/sharing/CreateShareForm';
import { OperationalGuide } from '@/components/common/OperationalGuide';
import { useShareStore, selectSharesForProperty, getShareUrl } from '@/stores/share-store';
import type { PropertyShare } from '@/types/share';

import noPropertiesPng from '@/assets/illustrations/empty/no-properties.png';

interface ShareManagerProps {
  propertyId: string;
  propertyName: string;
}

function formatExpiration(share: PropertyShare): string {
  if (!share.expiresAt) return 'Never expires';
  const d = parseISO(share.expiresAt);
  if (d < new Date()) return 'Expired';
  return `Expires ${format(d, 'MMM d, yyyy')}`;
}

function ShareRow({ share, onRevoke, onDelete }: {
  share: PropertyShare;
  onRevoke: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const url = getShareUrl(share.token);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — fall back to selecting the URL
    }
  }

  async function handleNativeShare() {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: 'Ridgen property', url });
      } catch {
        // User cancelled or API unavailable — no-op
      }
    }
  }

  return (
    <div
      className="bg-[var(--color-elevated)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-3"
      data-testid={`share-row-${share.id}`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-body-sm font-medium text-[var(--color-text-primary)] truncate">
            {share.label || 'Share link'}
          </span>
          <span className="text-body-xs text-[var(--color-text-muted)]">
            {formatExpiration(share)}
            {' · '}
            {share.accessCount === 0
              ? 'Never opened'
              : `Opened ${share.accessCount} time${share.accessCount === 1 ? '' : 's'}`}
          </span>
          {share.passwordHash && (
            <span className="inline-flex items-center gap-1 text-body-xs text-[var(--color-text-muted)] mt-0.5">
              <EyeOff size={11} />
              Password protected
            </span>
          )}
        </div>

        {/* Copy + share buttons */}
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            onClick={() => void handleCopy()}
            className="px-2 py-1.5 text-body-xs"
            data-testid={`copy-button-${share.id}`}
          >
            <Copy size={13} className="mr-1" />
            {copied ? 'Copied!' : 'Copy'}
          </Button>

          {typeof navigator.share === 'function' && (
            <Button
              variant="ghost"
              onClick={() => void handleNativeShare()}
              className="px-2 py-1.5 text-body-xs"
              aria-label="Share via system share sheet"
              data-testid={`native-share-button-${share.id}`}
            >
              <Share2 size={13} />
            </Button>
          )}
        </div>
      </div>

      {/* URL display */}
      <div className="px-2 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-body-xs text-[var(--color-text-muted)] truncate select-all">
        {url}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        {!confirmDelete ? (
          <>
            <button
              type="button"
              onClick={() => onRevoke(share.id)}
              className="text-body-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
              data-testid={`revoke-button-${share.id}`}
            >
              Deactivate
            </button>
            <span className="text-[var(--color-border)]">·</span>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="text-body-xs text-red-400 hover:text-red-300 transition-colors"
              data-testid={`delete-button-${share.id}`}
            >
              Delete
            </button>
          </>
        ) : (
          <>
            <span className="text-body-xs text-[var(--color-text-secondary)]">Delete this link?</span>
            <button
              type="button"
              onClick={() => onDelete(share.id)}
              className="text-body-xs text-red-400 hover:text-red-300 flex items-center gap-1"
              data-testid={`confirm-delete-button-${share.id}`}
            >
              <Trash2 size={12} />
              Yes, delete
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="text-body-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function ShareManager({ propertyId, propertyName }: ShareManagerProps) {
  const { sharesByProperty, isLoading, error, loadShares, createShare, revokeShare, deleteShare } =
    useShareStore();
  const shares = selectSharesForProperty(sharesByProperty, propertyId);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    void loadShares(propertyId);
  }, [propertyId, loadShares]);

  async function handleCreate(options: Parameters<typeof createShare>[1]) {
    setIsCreating(true);
    setCreateError(null);
    try {
      await createShare(propertyId, options);
      setShowCreateForm(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create share link');
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="share-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-display-sm text-[var(--color-text-display)]">
            Share {propertyName}
          </h2>
          <p className="text-body-xs text-[var(--color-text-muted)] mt-0.5">
            Share a read-only view with hunting partners — no account required to view.
          </p>
        </div>
        {!showCreateForm && (
          <Button
            variant="primary"
            onClick={() => setShowCreateForm(true)}
            className="shrink-0 text-body-xs px-3 py-2"
            data-testid="new-share-button"
          >
            + New link
          </Button>
        )}
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 shadow-[var(--shadow-card)]">
          <p className="text-body-sm font-medium text-[var(--color-text-primary)] mb-4">
            New share link
          </p>
          {createError && (
            <p className="text-body-sm text-red-400 mb-3" data-testid="create-error">
              {createError}
            </p>
          )}
          <CreateShareForm
            onSubmit={handleCreate}
            onCancel={() => { setShowCreateForm(false); setCreateError(null); }}
            isSubmitting={isCreating}
          />
        </div>
      )}

      {/* Shares list */}
      {isLoading && (
        <p className="text-body-sm text-[var(--color-text-muted)]" data-testid="loading-state">
          Loading…
        </p>
      )}

      {error && !isLoading && (
        <p className="text-body-sm text-red-400" data-testid="error-state">
          {error}
        </p>
      )}

      {!isLoading && shares.length === 0 && !showCreateForm && (
        <OperationalGuide
          illustration={noPropertiesPng}
          heading="No Shares Yet"
          subtext="Share your property with a guest — no account required to view."
          ctaLabel="Create Share Link"
          onCtaClick={() => setShowCreateForm(true)}
          compact
          data-guide-id="share-manager-empty"
        />
      )}

      {shares.map((share) => (
        <ShareRow
          key={share.id}
          share={share}
          onRevoke={(id) => void revokeShare(id, propertyId)}
          onDelete={(id) => void deleteShare(id, propertyId)}
        />
      ))}
    </div>
  );
}
